import React, { useState, useCallback } from 'react'
import {
  ActivityIndicator,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  View,
  Alert,
  ScrollView,
} from 'react-native'
import { useTheme, useNavigation, useRoute, RouteProp } from '@react-navigation/native'
import { Button, Icon, Screen, Text } from '@components'
import { Theme } from '@theme'
import { useLanguage } from '@context'
import { useFtStartSocialImport, useFtListSocialImports } from '@hooks'
import { translateApiError } from '@utils'
import type { SocialImportItem } from '@hooks'
import type { ContactStackParamList } from '../../../navigation/navigator/ContactNavigator'

const networkLabels: Record<string, string> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  twitter: 'Twitter/X',
  linkedin: 'LinkedIn',
}

export const FtSocialImportNetwork = () => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)
  const navigation = useNavigation()
  const route = useRoute<RouteProp<ContactStackParamList, 'FtSocialImportNetwork'>>()
  const { network } = route.params

  const networkLabel = networkLabels[network] ?? network

  const [username, setUsername] = useState('')
  const startImport = useFtStartSocialImport()
  const { data: importsData, refetch } = useFtListSocialImports()

  const networkImports = (importsData?.imports ?? []).filter((i) => i.network === network)
  const succeededImports = networkImports.filter((i) => i.status === 'succeeded')
  const runningImports = networkImports.filter(
    (i) => i.status === 'running' || i.status === 'pending',
  )
  const failedImports = networkImports.filter(
    (i) => i.status === 'failed' || i.status === 'timed_out',
  )

  const handleStartImport = useCallback(async () => {
    if (!username.trim()) return

    try {
      await startImport.mutateAsync({
        network: network as any,
        username: username.trim(),
      })
      setUsername('')
      refetch()
      Alert.alert(
        t('ftSocialImport.launched.title'),
        t('ftSocialImport.launched.networkMessage', { network: networkLabel }),
      )
    } catch (e: unknown) {
      // Translate API `{ code, message, params }` envelope instead of
      // leaking the raw axios error message into the toast.
      Alert.alert(t('ftSocialImport.error.title'), translateApiError(t, e).message)
    }
  }, [network, username, startImport, networkLabel, refetch, t])

  const handleViewResults = (importItem: SocialImportItem) => {
    ;(navigation as any).navigate('FtSocialImportResults', {
      importId: importItem.id,
    })
  }

  const placeholderText =
    network === 'linkedin'
      ? t('ftSocialImport.placeholder.linkedin')
      : network === 'facebook'
        ? t('ftSocialImport.placeholder.facebook')
        : network === 'twitter'
          ? t('ftSocialImport.placeholder.twitter')
          : t('ftSocialImport.placeholder.instagram')

  return (
    <Screen>
      <View style={styles.header}>
        <Icon name="arrowBack" onPress={() => navigation.goBack()} />
        <Text type="titleBold" color={theme.colors.black}>
          {networkLabel}
        </Text>
        <View style={{ width: 24 }} />
      </View>

      <ScrollView
        style={styles.scrollContent}
        contentContainerStyle={styles.scrollContentContainer}
      >
        {/* Existing imports section */}
        {networkImports.length > 0 && (
          <View style={styles.section}>
            <Text type="bodyBold" color={theme.colors.black} style={styles.sectionTitle}>
              {t('ftSocialImport.previousImports')}
            </Text>

            {succeededImports.map((item) => (
              <TouchableOpacity
                key={item.id}
                style={styles.importItem}
                onPress={() => handleViewResults(item)}
              >
                <View style={styles.importItemLeft}>
                  <Text type="body" color={theme.colors.black} numberOfLines={1}>
                    @{item.username}
                  </Text>
                  <Text type="bodyExtraSmall" color={theme.colors.green}>
                    {t('ftSocialImport.profilesFound', { count: item.totalResults ?? 0 })}
                  </Text>
                </View>
                <Icon name="arrowRight" size={16} color={theme.colors.primary} />
              </TouchableOpacity>
            ))}

            {runningImports.map((item) => (
              <View key={item.id} style={styles.importItem}>
                <View style={styles.importItemLeft}>
                  <Text type="body" color={theme.colors.black} numberOfLines={1}>
                    @{item.username}
                  </Text>
                  <Text type="bodyExtraSmall" color={theme.colors.primary}>
                    {t('ftSocialImport.inProgress')}
                  </Text>
                </View>
                <ActivityIndicator size="small" color={theme.colors.primary} />
              </View>
            ))}

            {failedImports.map((item) => (
              <View key={item.id} style={styles.importItem}>
                <View style={styles.importItemLeft}>
                  <Text type="body" color={theme.colors.black} numberOfLines={1}>
                    @{item.username}
                  </Text>
                  <Text type="bodyExtraSmall" color={theme.colors.error}>
                    {t('ftSocialImport.failed')}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        )}

        {/* New import section */}
        <View style={styles.section}>
          <Text type="bodyBold" color={theme.colors.black} style={styles.sectionTitle}>
            {t('ftSocialImport.newImport')}
          </Text>
          <Text type="bodyExtraSmall" color={theme.colors.secondary} style={styles.sectionDesc}>
            {network === 'linkedin'
              ? t('ftSocialImport.networkDescription.linkedin')
              : t('ftSocialImport.networkDescription.generic', { network: networkLabel })}
          </Text>

          <TextInput
            style={styles.input}
            placeholder={placeholderText}
            placeholderTextColor={theme.colors.lightGrey}
            value={username}
            onChangeText={setUsername}
            autoCapitalize="none"
            autoCorrect={false}
          />

          <Button
            title={t('ftSocialImport.startImport')}
            onPress={handleStartImport}
            disabled={!username.trim() || startImport.isPending}
            containerStyle={styles.button}
          />
        </View>
      </ScrollView>
    </Screen>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.md,
    },
    scrollContent: {
      flex: 1,
    },
    scrollContentContainer: {
      paddingHorizontal: theme.spacing.lg,
      paddingBottom: theme.spacing.xxl,
    },
    section: {
      marginTop: theme.spacing.lg,
    },
    sectionTitle: {
      marginBottom: theme.spacing.sm,
    },
    sectionDesc: {
      marginBottom: theme.spacing.md,
    },
    importItem: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: theme.spacing.md,
      paddingHorizontal: theme.spacing.md,
      borderWidth: 1,
      borderColor: theme.colors.grey,
      borderRadius: theme.spacing.sm,
      marginBottom: theme.spacing.sm,
      backgroundColor: theme.colors.white,
    },
    importItemLeft: {
      flex: 1,
      gap: 2,
    },
    input: {
      borderWidth: 1,
      borderColor: theme.colors.grey,
      borderRadius: theme.spacing.sm,
      paddingVertical: theme.spacing.sm,
      paddingHorizontal: theme.spacing.md,
      fontSize: 16,
      color: theme.colors.black,
    },
    button: {
      marginTop: theme.spacing.md,
    },
  })
